import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CheckCircle, AlertTriangle, XCircle, Clock, HelpCircle, Link2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { GROUPS } from './classificationGroups';

export const QUAL_STATUS = {
  atendido: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700', label: 'Atendido' },
  vencendo: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', label: 'Vencendo' },
  vencido: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', label: 'Vencido' },
  nao_atendido: { icon: XCircle, color: 'text-slate-400', bg: 'bg-slate-50', badge: 'bg-slate-100 text-slate-700', label: 'Pendente' },
  nao_atende: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', label: 'Não atende' },
  nao_verificado: { icon: HelpCircle, color: 'text-slate-400', bg: '', badge: 'bg-slate-100 text-slate-600', label: 'Não verificado' },
  dispensado: { icon: XCircle, color: 'text-purple-500', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', label: 'Dispensado' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '');

const ReqActions = ({ req, grupo, onVincular, onRemove, onDispensar, onMark }) => {
  if (req.comprovacao?.fonte === 'treinamento_sistema') return null;
  if (req.comprovacao) {
    return (
      <Button variant="ghost" size="sm" onClick={() => onRemove(req)} className="text-red-600 hover:text-red-700" data-testid="qual-remove-btn">
        {req.status === 'dispensado' ? 'Reativar' : 'Remover'}
      </Button>
    );
  }
  return (
    <div className="flex gap-1 justify-end">
      {grupo === 'requisito' && (
        <>
          <Button variant="outline" size="sm" className="text-green-700 border-green-300" onClick={() => onMark(req, 'atendido')} data-testid="qual-mark-atende-btn">
            <ThumbsUp className="w-3.5 h-3.5 mr-1" /> Atende
          </Button>
          <Button variant="outline" size="sm" className="text-red-700 border-red-300" onClick={() => onMark(req, 'nao_atende')} data-testid="qual-mark-nao-atende-btn">
            <ThumbsDown className="w-3.5 h-3.5 mr-1" /> Não atende
          </Button>
        </>
      )}
      <Button variant="outline" size="sm" onClick={() => onVincular(req)} data-testid="qual-vincular-btn">
        <Link2 className="w-4 h-4 mr-1" /> Vincular
      </Button>
      {!req.obrigatorio && (
        <Button variant="ghost" size="sm" onClick={() => onDispensar(req)} className="text-purple-600" title="Dispensar requisito" data-testid="qual-dispensar-btn">
          <XCircle className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export const QualificationGroupTable = ({ grupo, requisitos, handlers }) => {
  const g = GROUPS[grupo];
  const Icon = g.icon;
  const byCat = requisitos.reduce((acc, r) => ({ ...acc, [r.categoria]: [...(acc[r.categoria] || []), r] }), {});
  return (
    <div className={`rounded-xl border p-4 ${g.box}`} data-testid={`qual-group-${grupo}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${g.title}`} />
        <h3 className={`font-semibold ${g.title}`}>{g.label}</h3>
        <span className="text-xs text-slate-500">— {g.hint}</span>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${g.chip}`}>{requisitos.length}</span>
      </div>
      {requisitos.length === 0 ? (
        <p className="text-sm text-slate-500 italic">Nenhum item definido na descrição do cargo</p>
      ) : (
        Object.entries(byCat).map(([cat, items]) => (
          <div key={cat} className="mb-4 last:mb-0 bg-white rounded-lg border">
            <p className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{cat}</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[34%]">{grupo === 'treinamento' ? 'Treinamento' : 'Requisito'}</TableHead>
                  <TableHead className="w-[30%]">Comprovação</TableHead>
                  <TableHead className="w-[14%]">Status</TableHead>
                  <TableHead className="w-[22%] text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((req, idx) => {
                  const st = QUAL_STATUS[req.status] || QUAL_STATUS.nao_verificado;
                  const StIcon = st.icon;
                  const label = grupo === 'requisito' && req.status === 'atendido' ? 'Atende' : st.label;
                  return (
                    <TableRow key={`${req.tipo}-${idx}`} className={st.bg} data-testid={`qual-row-${grupo}-${idx}`}>
                      <TableCell className="font-medium">
                        {req.requisito}
                        {req.obrigatorio ? (
                          <Badge variant="outline" className="ml-2 text-xs">Obrigatório</Badge>
                        ) : (
                          <Badge variant="outline" className="ml-2 text-xs text-slate-500">Desejável</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {req.comprovacao ? (
                          <div>
                            <div className="text-sm">{req.comprovacao.descricao}</div>
                            {req.comprovacao.fonte === 'treinamento_sistema' && <Badge variant="outline" className="text-xs mt-1">Automático do sistema</Badge>}
                            {req.comprovacao.data_vinculacao && req.comprovacao.fonte !== 'treinamento_sistema' && (
                              <div className="text-xs text-slate-500 mt-1">Registrado em {fmtDate(req.comprovacao.data_vinculacao)}</div>
                            )}
                            {req.data_validade && <div className="text-xs text-slate-500 mt-1">Válido até: {fmtDate(req.data_validade)}</div>}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">Não vinculado</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <StIcon className={`w-5 h-5 ${st.color}`} />
                          <Badge className={st.badge} data-testid="qual-status-badge">{label}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <ReqActions req={req} grupo={grupo} {...handlers} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ))
      )}
    </div>
  );
};
